import { CampaignProductType, CampaignSource } from 'utils/constants/campaign';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import { BiddingStrategy } from 'store/models/auctionData';

export const DEFAULT_PAGE_NUMBER = 1;

export enum CampaignsTab {
    All = 'all',
    Active = 'active',
    Paused = 'paused',
    Attention = 'attention',
    Archived = 'archived',
}

export enum CampaignFrontendStatus {
    Active = 'ACTIVE',
    Blocked = 'BLOCKED',
    Archived = 'ARCHIVED',
    ActiveWithDelayedStart = 'ACTIVE_WITH_DELAYED_START',
    InactiveDueToEndDate = 'INACTIVE_DUE_TO_END_DATE',
    BudgetExceeded = 'BUDGET_EXCEEDED',
    DailyBudgetExceedStatus = 'DAILY_BUDGET_EXCEEDED',
    Unknown = 'UNKNOWN',
}

export enum CampaignArchiveReason {
    ArchivedByUser = 'ARCHIVED_BY_USER',
    ArchivedVacancy = 'ARCHIVED_VACANCY',
    ExpiredAccount = 'EXPIRED_ACCOUNT',
    ObsoleteCampaign = 'OBSOLETE_CAMPAIGN',
    OldBlockedCampaign = 'OLD_BLOCKED_CAMPAIGN',
    OldPrices = 'OLD_PRICES',
    OldSource = 'OLD_SOURCE',
    Transfer = 'TRANSFER',
    WrongAccount = 'WRONG_ACCOUNT',
}

export enum CampaignBannerModerationStatus {
    New = 'NEW',
    Moderated = 'MODERATED',
    Banned = 'BANNED',
}

export interface CampaignBanner {
    id: number;
    url: string;
    imageUrl: string;
    moderationStatus: CampaignBannerModerationStatus;
}

export interface CampaignAreas {
    include: number[];
    exclude: number[];
}

enum AdvertisingServiceType {
    Information = 'INFORMATION',
    Advertising = 'ADVERTISING',
}

export interface DashboardCampaign {
    id: number;
    name: string;
    frontendStatus: CampaignFrontendStatus;
    archiveReason: CampaignArchiveReason;
    banners: CampaignBanner[];
    productType: CampaignProductType;
    areas: CampaignAreas;
    professionalRoles: string[];
    biddingStrategy: BiddingStrategy | null;
    budget: number;
    bid: number;
    sources: CampaignSource[];
    createdAt: string;
    startAt: string | null;
    endAt: string | null;
    stopAt: string | null;
    advertisingServiceType: AdvertisingServiceType;
    vacancyId: number;
    vacancyName: string;
}

export interface CampaignStatistics {
    clicks: number;
    shows: number;
    amount: number;
    cost: number;
    averagePricePerClickInCents: number;
    ctr: number;
    responses: number;
    responsesBeforeClickme: number;
    vacancyViews: number;
}

export interface CampaignDetails {
    createdByName: string;
    vacancyManagerName: string;
}

export type CampaignsCounts = Record<CampaignsTab, number>;

// ----------------------- actions -------------------------------------
const SET_CAMPAIGNS_COUNTS = 'SET_CAMPAIGNS_COUNTS';
const SET_CAMPAIGNS_CURRENT_TAB = 'SET_CAMPAIGNS_CURRENT_TAB';
const SET_CAMPAIGNS_CURRENT_PAGE = 'SET_CAMPAIGNS_CURRENT_PAGE';
const SET_CAMPAIGNS = 'SET_CAMPAIGNS';
const SET_IS_CAMPAIGNS_LOADING = 'SET_IS_CAMPAIGNS_LOADING';
const SET_CAMPAIGNS_STATISTICS = 'SET_CAMPAIGNS_STATISTICS';
const SET_IS_CAMPAIGNS_STATISTICS_LOADING = 'SET_IS_CAMPAIGNS_STATISTICS_LOADING';
const SET_CAMPAIGNS_DETAILS = 'SET_CAMPAIGNS_DETAILS';
const SET_IS_CAMPAIGNS_DETAILS_LOADING = 'SET_IS_CAMPAIGNS_DETAILS_LOADING';
const SET_CAMPAIGN_REGIONS = 'SET_CAMPAIGN_REGIONS';
const SET_IS_BUDGET_INCREASE_MODAL_VISIBLE = 'SET_IS_BUDGET_INCREASE_MODAL_VISIBLE';
const SET_CAMPAIGN_IDS_FOR_BUDGET_INCREASE = 'SET_CAMPAIGN_IDS_FOR_BUDGET_INCREASE';
const SET_CHECKED_CAMPAIGN_IDS = 'SET_CHECKED_CAMPAIGN_IDS';
const DELETE_ID_FROM_CHECKED_CAMPAIGN_IDS = 'DELETE_ID_FROM_CHECKED_CAMPAIGN_IDS';
const SET_ARCHIVE_CAMPAIGN_ALERT = 'SET_ARCHIVE_CAMPAIGN_ALERT';
const SET_STOP_CAMPAIGN_ALERT = 'SET_STOP_CAMPAIGN_ALERT';
const SET_MODERATION_FAILED_ALERT = 'SET_MODERATION_FAILED_ALERT';
const SET_IS_CAMPAIGN_INSUFFICIENT_BALANCE_ALERT_VISIBLE = 'SET_IS_CAMPAIGN_INSUFFICIENT_BALANCE_ALERT_VISIBLE';
const SET_INACTIVE_DUE_TO_END_DATE_ALERT = 'SET_INACTIVE_DUE_TO_END_DATE_ALERT';
const SET_BUDGET_EXCEEDED_ALERT = 'SET_BUDGET_EXCEEDED_ALERT';

interface PayloadTypes {
    [SET_CAMPAIGNS_COUNTS]: CampaignsCounts;
    [SET_CAMPAIGNS_CURRENT_TAB]: CampaignsTab;
    [SET_CAMPAIGNS_CURRENT_PAGE]: number;
    [SET_CAMPAIGNS]: Map<number, DashboardCampaign>;
    [SET_IS_CAMPAIGNS_LOADING]: boolean;
    [SET_CAMPAIGNS_STATISTICS]: Record<string, CampaignStatistics> | null;
    [SET_IS_CAMPAIGNS_STATISTICS_LOADING]: boolean;
    [SET_CAMPAIGNS_DETAILS]: Record<string, CampaignDetails> | null;
    [SET_IS_CAMPAIGNS_DETAILS_LOADING]: boolean;
    [SET_CAMPAIGN_REGIONS]: Record<number, string>;
    [SET_IS_BUDGET_INCREASE_MODAL_VISIBLE]: boolean;
    [SET_CAMPAIGN_IDS_FOR_BUDGET_INCREASE]: Set<number>;
    [SET_CHECKED_CAMPAIGN_IDS]: Set<number>;
    [DELETE_ID_FROM_CHECKED_CAMPAIGN_IDS]: number;
    [SET_ARCHIVE_CAMPAIGN_ALERT]: { isVisible: boolean; campaignId: number | null };
    [SET_STOP_CAMPAIGN_ALERT]: { isVisible: boolean; campaignIds: Set<number> | null };
    [SET_MODERATION_FAILED_ALERT]: { isVisible: boolean; campaignId: number | null };
    [SET_IS_CAMPAIGN_INSUFFICIENT_BALANCE_ALERT_VISIBLE]: boolean;
    [SET_INACTIVE_DUE_TO_END_DATE_ALERT]: { isVisible: boolean; campaignId: number | null };
    [SET_BUDGET_EXCEEDED_ALERT]: { isVisible: boolean; campaignId: number | null };
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setCampaignsCounts = ActionCreator(SET_CAMPAIGNS_COUNTS);
export const setCampaignsCurrentTab = ActionCreator(SET_CAMPAIGNS_CURRENT_TAB);
export const setCampaignsCurrentPage = ActionCreator(SET_CAMPAIGNS_CURRENT_PAGE);
export const setCampaigns = ActionCreator(SET_CAMPAIGNS);
export const setIsCampaignsLoading = ActionCreator(SET_IS_CAMPAIGNS_LOADING);
export const setCampaignsStatistics = ActionCreator(SET_CAMPAIGNS_STATISTICS);
export const setIsCampaignsStatisticsLoading = ActionCreator(SET_IS_CAMPAIGNS_STATISTICS_LOADING);
export const setCampaignsDetails = ActionCreator(SET_CAMPAIGNS_DETAILS);
export const setIsCampaignsDetailsLoading = ActionCreator(SET_IS_CAMPAIGNS_DETAILS_LOADING);
export const setCampaignRegions = ActionCreator(SET_CAMPAIGN_REGIONS);
export const setIsBudgetIncreaseModalVisible = ActionCreator(SET_IS_BUDGET_INCREASE_MODAL_VISIBLE);
export const setCampaignIdsForBudgetIncrease = ActionCreator(SET_CAMPAIGN_IDS_FOR_BUDGET_INCREASE);
export const setCheckedCampaignIds = ActionCreator(SET_CHECKED_CAMPAIGN_IDS);
export const deleteIdFromCheckedCampaignIds = ActionCreator(DELETE_ID_FROM_CHECKED_CAMPAIGN_IDS);
export const setArchiveCampaignAlert = ActionCreator(SET_ARCHIVE_CAMPAIGN_ALERT);
export const setStopCampaignAlert = ActionCreator(SET_STOP_CAMPAIGN_ALERT);
export const setModerationFailedAlert = ActionCreator(SET_MODERATION_FAILED_ALERT);
export const setIsCampaignInsufficientBalanceAlertVisible = ActionCreator(
    SET_IS_CAMPAIGN_INSUFFICIENT_BALANCE_ALERT_VISIBLE
);
export const setInactiveDueToEndDateAlert = ActionCreator(SET_INACTIVE_DUE_TO_END_DATE_ALERT);
export const setBudgetExceededAlert = ActionCreator(SET_BUDGET_EXCEEDED_ALERT);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface CampaignsDashboardState {
    campaignsCounts: CampaignsCounts;
    campaignsCurrentTab: CampaignsTab;
    campaignsCurrentPage: number;
    campaigns: Map<number, DashboardCampaign>;
    isCampaignsLoading: boolean;
    campaignsStatistics: Record<string, CampaignStatistics> | null;
    isCampaignsStatisticsLoading: boolean;
    campaignsDetails: Record<string, CampaignDetails> | null;
    isCampaignsDetailsLoading: boolean;
    campaignRegions: Record<number, string>;
    isBudgetIncreaseModalVisible: boolean;
    campaignIdsForBudgetIncrease: Set<number>;
    checkedCampaignIds: Set<number>;
    statusChangeCampaignId: number | null;
    campaignIdsForStop: Set<number> | null;
    isArchiveCampaignAlertVisible: boolean;
    isStopCampaignAlertVisible: boolean;
    isModerationFailedAlertVisible: boolean;
    isCampaignInsufficientBalanceAlertVisible: boolean;
    isInactiveDueToEndDateAlertVisible: boolean;
    isBudgetExceededAlertVisible: boolean;
}

export const CAMPAIGNS_DASHBOARD_INITIAL_STATE: CampaignsDashboardState = {
    campaignsCounts: {
        [CampaignsTab.All]: 0,
        [CampaignsTab.Active]: 0,
        [CampaignsTab.Paused]: 0,
        [CampaignsTab.Attention]: 0,
        [CampaignsTab.Archived]: 0,
    },
    campaignsCurrentTab: CampaignsTab.Active,
    campaignsCurrentPage: DEFAULT_PAGE_NUMBER,
    campaigns: new Map(),
    isCampaignsLoading: true,
    campaignsStatistics: null,
    isCampaignsStatisticsLoading: true,
    campaignsDetails: null,
    isCampaignsDetailsLoading: true,
    campaignRegions: {},
    isBudgetIncreaseModalVisible: false,
    campaignIdsForBudgetIncrease: new Set(),
    checkedCampaignIds: new Set(),
    statusChangeCampaignId: null,
    campaignIdsForStop: null,
    isArchiveCampaignAlertVisible: false,
    isStopCampaignAlertVisible: false,
    isModerationFailedAlertVisible: false,
    isCampaignInsufficientBalanceAlertVisible: false,
    isInactiveDueToEndDateAlertVisible: false,
    isBudgetExceededAlertVisible: false,
};

export default createReducer<CampaignsDashboardState, PayloadTypes>(CAMPAIGNS_DASHBOARD_INITIAL_STATE, {
    [SET_CAMPAIGNS_COUNTS]: (state, { payload }) => ({
        ...state,
        campaignsCounts: payload,
    }),
    [SET_CAMPAIGNS_CURRENT_TAB]: (state, { payload }) => ({
        ...state,
        campaignsCurrentTab: payload,
    }),
    [SET_CAMPAIGNS_CURRENT_PAGE]: (state, { payload }) => ({
        ...state,
        campaignsCurrentPage: payload,
    }),
    [SET_CAMPAIGNS]: (state, { payload }) => ({
        ...state,
        campaigns: payload,
    }),
    [SET_IS_CAMPAIGNS_LOADING]: (state, { payload }) => ({
        ...state,
        isCampaignsLoading: payload,
    }),
    [SET_CAMPAIGNS_STATISTICS]: (state, { payload }) => ({
        ...state,
        campaignsStatistics: payload,
    }),
    [SET_IS_CAMPAIGNS_STATISTICS_LOADING]: (state, { payload }) => ({
        ...state,
        isCampaignsStatisticsLoading: payload,
    }),
    [SET_CAMPAIGNS_DETAILS]: (state, { payload }) => ({
        ...state,
        campaignsDetails: payload,
    }),
    [SET_IS_CAMPAIGNS_DETAILS_LOADING]: (state, { payload }) => ({
        ...state,
        isCampaignsDetailsLoading: payload,
    }),
    [SET_CAMPAIGN_REGIONS]: (state, { payload }) => ({
        ...state,
        campaignRegions: payload,
    }),
    [SET_IS_BUDGET_INCREASE_MODAL_VISIBLE]: (state, { payload }) => ({
        ...state,
        isBudgetIncreaseModalVisible: payload,
    }),
    [SET_CAMPAIGN_IDS_FOR_BUDGET_INCREASE]: (state, { payload }) => ({
        ...state,
        campaignIdsForBudgetIncrease: payload,
    }),
    [SET_CHECKED_CAMPAIGN_IDS]: (state, { payload }) => ({
        ...state,
        checkedCampaignIds: payload,
    }),
    [DELETE_ID_FROM_CHECKED_CAMPAIGN_IDS]: (state, { payload }) => ({
        ...state,
        checkedCampaignIds: new Set([...state.checkedCampaignIds].filter((id) => id !== payload)),
    }),
    [SET_ARCHIVE_CAMPAIGN_ALERT]: (state, { payload: { isVisible, campaignId } }) => ({
        ...state,
        isArchiveCampaignAlertVisible: isVisible,
        statusChangeCampaignId: isVisible ? campaignId : null,
    }),
    [SET_STOP_CAMPAIGN_ALERT]: (state, { payload: { isVisible, campaignIds } }) => ({
        ...state,
        isStopCampaignAlertVisible: isVisible,
        campaignIdsForStop: isVisible ? campaignIds : null,
    }),
    [SET_MODERATION_FAILED_ALERT]: (state, { payload: { isVisible, campaignId } }) => ({
        ...state,
        isModerationFailedAlertVisible: isVisible,
        statusChangeCampaignId: isVisible ? campaignId : null,
    }),
    [SET_IS_CAMPAIGN_INSUFFICIENT_BALANCE_ALERT_VISIBLE]: (state, { payload }) => ({
        ...state,
        isCampaignInsufficientBalanceAlertVisible: payload,
    }),
    [SET_INACTIVE_DUE_TO_END_DATE_ALERT]: (state, { payload: { isVisible, campaignId } }) => ({
        ...state,
        isInactiveDueToEndDateAlertVisible: isVisible,
        statusChangeCampaignId: isVisible ? campaignId : null,
    }),
    [SET_BUDGET_EXCEEDED_ALERT]: (state, { payload: { isVisible, campaignId } }) => ({
        ...state,
        isBudgetExceededAlertVisible: isVisible,
        statusChangeCampaignId: isVisible ? campaignId : null,
    }),
});
// ----------------------- /reducer --------------------------------------
